"use client";

import { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { hardcodedContent } from "@/app/(site)/_lib/global-content";
import { HomePageQueryResult } from "@/sanity.types";
import HeroTextAnimations from "../../../utils/hero-text-animations/HeroTextAnimations";
import AnimatedToggleArrow from "../../../utils/animated-toggle-arrow/AnimatedToggleArrow";

type TextBlocksProps = {
	texts: NonNullable<HomePageQueryResult>["aboutCompanyTexts"]["texts"];
};

export default function TextBlocks({ texts }: TextBlocksProps) {
	const [activeSlide, setActiveSlide] = useState(0);
	const [direction, setDirection] = useState(0); // 1 for down, -1 for up
	const sectionRef = useRef<HTMLDivElement | null>(null);

	useEffect(() => {
		const handleScroll = () => {
			if (!sectionRef.current) return;

			const section = sectionRef.current.closest('section[id="aboutCompany"]') as HTMLElement;
			if (!section) return;

			const sectionRect = section.getBoundingClientRect();
			const sectionTop = sectionRect.top;
			const sectionHeight = sectionRect.height;
			const viewportHeight = window.innerHeight;

			const scrollProgress = Math.max(0, Math.min(1, -sectionTop / (sectionHeight - viewportHeight)));

			const slideIndex = Math.floor(scrollProgress * 3); // Fixed 3 blocks
			const clampedIndex = Math.max(0, Math.min(2, slideIndex));

			if (clampedIndex !== activeSlide) {
				setDirection(clampedIndex > activeSlide ? 1 : -1);
				setActiveSlide(clampedIndex);
			}
		};

		window.addEventListener("scroll", handleScroll);
		handleScroll();

		return () => {
			window.removeEventListener("scroll", handleScroll);
		};
	}, [activeSlide]);

	// Function to render different JSX layouts based on block index
	const renderBlockContent = (blockIndex: number) => {
		switch (blockIndex) {
			case 0:
				// First block layout
				return (
					<div className="flex flex-col gap-5">
						<div className="flex flex-col gap-y-2">
							<h5 className="text-h5 sm:text-h2 text-graphite-black">
								{hardcodedContent.homePage.aboutCompanyPartOne.headline}
							</h5>

							<div className="flex items-center gap-x-[0.83rem]">
								<span className="text-h5 sm:text-h2 text-graphite-black">
									{hardcodedContent.homePage.aboutCompanyPartOne.headlineLine2}
								</span>
								<HeroTextAnimations txts={hardcodedContent.homePage.aboutCompanyPartOne.headlineLineAnimationText} />
							</div>
						</div>

						<p className="text-body-m sm:text-body-l text-graphite-black/70 whitespace-pre-wrap">{texts[0].text}</p>
					</div>
				);

			case 1:
				// Second block layout
				return (
					<div className="flex flex-col gap-5">
						<div className="flex flex-col gap-y-2">
							<h5 className="text-h5 sm:text-h2 text-graphite-black">
								{hardcodedContent.homePage.aboutCompanyPartTwo.headline}
							</h5>

							<div className="flex items-center gap-x-[0.83rem]">
								<AnimatedToggleArrow />
								<span className="text-h5 sm:text-h2 text-graphite-black">
									{hardcodedContent.homePage.aboutCompanyPartTwo.headlineLine2}
								</span>
							</div>
						</div>

						<p className="text-body-m sm:text-body-l text-graphite-black/70 whitespace-pre-wrap">{texts[1].text}</p>
					</div>
				);

			case 2:
				// Third block layout
				return (
					<div className="flex flex-col gap-5">
						<div className="flex flex-col gap-y-2">
							<h5 className="text-h5 sm:text-h2 text-graphite-black">
								{hardcodedContent.homePage.aboutCompanyPartThree.headline}
							</h5>

							<div className="flex items-center gap-x-[0.83rem]">
								<span className="text-h5 sm:text-h2 text-graphite-black">
									{hardcodedContent.homePage.aboutCompanyPartThree.headlineLine2}
								</span>
								<div className="relative inline-flex h-[3rem] items-center sm:h-[4.5rem]">
									{/* Circle */}
									<div className="border-brown-middle transform-origin-left bg-brown-middle absolute left-0 flex h-[3rem] w-[3rem] animate-[circleMobile_4s_ease-in-out_infinite] items-center justify-center rounded-full border-[0.203rem] sm:h-[4.5rem] sm:w-[4.5rem] sm:animate-[circleDesktop_4s_ease-in-out_infinite]">
										<svg
											xmlns="http://www.w3.org/2000/svg"
											viewBox="0 0 18 18"
											fill="none"
											className="h-4 w-4 sm:h-6 sm:w-6"
										>
											<path
												d="M8.29443 12.5593C8.29443 11.1236 7.13055 9.95972 5.69482 9.95972L3.89502 9.95972C3.4533 9.95972 3.09521 9.60163 3.09521 9.15991C3.09521 8.71819 3.4533 8.36011 3.89502 8.36011L5.69482 8.36011C7.13055 8.36011 8.29443 7.19622 8.29443 5.7605L8.29443 3.96069C8.29443 3.51897 8.65252 3.16089 9.09424 3.16089C9.53596 3.16089 9.89404 3.51897 9.89404 3.96069L9.89404 5.7605C9.89404 7.19622 11.0579 8.36011 12.4937 8.36011L14.2954 8.36011C14.7371 8.36011 15.0952 8.71819 15.0952 9.15991C15.0952 9.60163 14.7371 9.95972 14.2954 9.95972L12.4946 9.95972C11.0584 9.95972 9.89404 11.124 9.89404 12.5603L9.89404 14.3611C9.89404 14.8028 9.53596 15.1609 9.09424 15.1609C8.65252 15.1609 8.29443 14.8028 8.29443 14.3611L8.29443 12.5593Z"
												fill="#504120"
											/>
											<rect
												x="0.694336"
												y="9.96436"
												width="1.6"
												height="16.6114"
												transform="rotate(-90 0.694336 9.96436)"
												fill="#504120"
											/>
											<rect
												x="9.89062"
												y="17.5627"
												width="1.6"
												height="16.6114"
												transform="rotate(180 9.89062 17.5627)"
												fill="#504120"
											/>
										</svg>
									</div>

									{/* Text */}
									<div className="border-brown-middle text-mobile sm:text-desktop relative z-10 inline-block animate-[textPushMobile_4s_ease-in-out_infinite] rounded-[2.593rem] border-[0.203rem] bg-white p-[0.7rem] sm:animate-[textPushDesktop_4s_ease-in-out_infinite] sm:p-[1.08rem]">
										<p className="text-rotation-mob sm:text-rotation-desk text-brown-middle whitespace-nowrap">
											{hardcodedContent.homePage.aboutCompanyPartThree.headlineLine3}
										</p>
									</div>
								</div>
							</div>
						</div>

						<p className="text-body-m sm:text-body-l text-graphite-black/70 whitespace-pre-wrap">{texts[2].text}</p>
					</div>
				);

			default:
				return null;
		}
	};

	const slideVariants = {
		enter: (direction: number) => ({
			y: direction > 0 ? 50 : -50,
			opacity: 0,
		}),
		center: {
			y: 0,
			opacity: 1,
		},
		exit: (direction: number) => ({
			y: direction < 0 ? 50 : -50,
			opacity: 0,
		}),
	};

	return (
		<div ref={sectionRef} className="relative overflow-hidden">
			<motion.div className="relative h-[16rem] sm:h-[19.5rem]">
				<div className="flex flex-col">
					<div className="flex flex-col gap-5">
						<AnimatePresence mode="popLayout" custom={direction}>
							<motion.div
								key={`content-${activeSlide}`}
								custom={direction}
								variants={slideVariants}
								initial="enter"
								animate="center"
								exit="exit"
								transition={{
									duration: 0.4,
									ease: "easeInOut",
								}}
								className="flex flex-col gap-5"
							>
								{renderBlockContent(activeSlide)}
							</motion.div>
						</AnimatePresence>
					</div>
				</div>
			</motion.div>
		</div>
	);
}
